// Pure cookie helpers for a broker-consumer worker. No Cloudflare-runtime
// dependency — they take and return strings and numbers, so the parse / build /
// expiry logic is unit-testable without a live worker (the untrusted `Cookie`
// header and the base64url JWT payload are exactly the type-system boundaries
// testing-philosophy targets).
//
// The three cookie names are the only thing that differed between the apps, so
// they arrive as a `CookieNames` and `createCookieKit` closes over them.

import {
  DEFAULT_BASE_PATH,
  MAX_SESSION_SECONDS,
  STATE_TTL_SECONDS,
  TRY_TTL_SECONDS,
} from "./config";

export interface CookieNames {
  /** Holds the raw broker JWT. HttpOnly. */
  readonly session: string;
  /** Short-TTL circuit-breaker attempt counter. HttpOnly. */
  readonly try: string;
  /** Sign-in CSRF nonce. */
  readonly state: string;
}

/** The names one app's cookies take, from its `cookiePrefix`. */
export function cookieNamesFor(prefix: string): CookieNames {
  return {
    session: `${prefix}-session`,
    try: `${prefix}-authtry`,
    state: `${prefix}-authstate`,
  };
}

/** Parse a `Cookie` request header into a name→value map (RFC 6265 loose). */
export function parseCookies(header: string | null): Record<string, string> {
  const out: Record<string, string> = {};
  if (!header) return out;
  for (const part of header.split(";")) {
    const eq = part.indexOf("=");
    if (eq < 0) continue;
    const name = part.slice(0, eq).trim();
    if (!name) continue;
    // First occurrence wins (RFC 6265 orders the more-specific/older cookie
    // first); don't let a later same-name entry overwrite it.
    if (name in out) continue;
    out[name] = part.slice(eq + 1).trim();
  }
  return out;
}

function serialize(
  name: string,
  value: string,
  path: string,
  maxAge: number,
  secure: boolean,
  httpOnly = true,
): string {
  const attrs = [`${name}=${value}`, `Path=${path}`];
  if (httpOnly) attrs.push("HttpOnly");
  attrs.push("SameSite=Lax", `Max-Age=${maxAge}`);
  // Host-only: deliberately NO `Domain` attribute — the session must never
  // leak to sibling `sara.ai` hosts. `Secure` is omitted only for local
  // `http://localhost` dev, where browsers exempt it.
  if (secure) attrs.push("Secure");
  return attrs.join("; ");
}

/**
 * A minted sign-in nonce: base64url, so it survives the broker's
 * `URLSearchParams` re-serialization of the callback query unchanged (a plain
 * base64 `+` would come back as a space). The length range accepts the 22 chars
 * `STATE_BYTES` produces with room either side, and rejects anything that
 * couldn't be one of ours — including the empty string, which is what makes
 * "absent cookie" and "absent param" impossible to collide into a match.
 */
const STATE_PATTERN = /^[A-Za-z0-9_-]{16,64}$/;

/** Whether `value` could be a nonce this app minted. */
export function isWellFormedState(value: string): boolean {
  return STATE_PATTERN.test(value);
}

/** Why a callback's `state` was refused, or `"ok"` when it checks out. */
export type StateCheck = "ok" | "missing_param" | "missing_cookie" | "mismatch";

/**
 * Compare the `state` a callback presented against the nonce this browser
 * stored when it started sign-in. FAIL-CLOSED and total: it returns a verdict
 * for every input and never throws.
 *
 * The `null` cases are enumerated separately and BEFORE any comparison on
 * purpose. A single `stored === presented` would pass `null === null` — i.e. a
 * forged callback to a browser that never started sign-in, which is precisely
 * the attack — and reduce the whole check to a no-op.
 *
 * `presented` is untrusted input; `isWellFormedState` bounds it before it is
 * compared or logged about, so no attacker-chosen string reaches either.
 */
export function checkState(
  stored: string | null,
  presented: string | null,
): StateCheck {
  if (presented === null || !isWellFormedState(presented)) {
    return "missing_param";
  }
  if (stored === null) return "missing_cookie";
  return stored === presented ? "ok" : "mismatch";
}

function decodeBase64UrlToString(segment: string): string {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Read the `exp` (seconds since epoch) out of a JWT WITHOUT verifying it — used
 * only to cheaply reject a dead token before serving it and to size the cookie.
 * This is a base64url + JSON.parse boundary over an untrusted string: it must
 * return `null` on anything malformed, never throw.
 */
export function decodeJwtExp(jwt: string): number | null {
  const parts = jwt.split(".");
  const [, payloadPart] = parts;
  if (parts.length !== 3 || payloadPart === undefined) return null;
  let payload: unknown;
  try {
    payload = JSON.parse(decodeBase64UrlToString(payloadPart));
  } catch {
    return null;
  }
  if (typeof payload !== "object" || payload === null) return null;
  const exp = (payload as Record<string, unknown>).exp;
  return typeof exp === "number" && Number.isFinite(exp) ? exp : null;
}

/**
 * Cookie `Max-Age` for a token expiring at `exp`: `min(exp - now, 30d)`, floored
 * at 0. Never negative (a past `exp` yields 0 → an immediately-expiring cookie).
 */
export function sessionMaxAge(exp: number, nowSeconds: number): number {
  const remaining = Math.floor(exp - nowSeconds);
  if (remaining <= 0) return 0;
  return Math.min(remaining, MAX_SESSION_SECONDS);
}

/** The name-bound half of the cookie layer: builders, clearers and readers. */
export interface CookieKit {
  readonly names: CookieNames;
  parseCookies: (header: string | null) => Record<string, string>;
  /**
   * `Set-Cookie` carrying the raw broker JWT. `maxAge` is derived by the caller
   * from the token's `exp` (see `sessionMaxAge`) so the cookie never outlives
   * the token it stores.
   */
  buildSessionCookie: (jwt: string, maxAge: number, secure: boolean) => string;
  /** Clears the session cookie (same attributes, `Max-Age=0`). */
  clearSessionCookie: (secure: boolean) => string;
  /** The short-TTL circuit-breaker attempt counter. */
  buildTryCookie: (count: number, secure: boolean) => string;
  /** Clears the attempt counter (on success / at the cap). */
  clearTryCookie: (secure: boolean) => string;
  /**
   * The one-shot sign-in nonce. Deliberately NOT `HttpOnly`, unlike every
   * other cookie here: the page used to write it with `document.cookie`, and a
   * browser silently ignores a write that would replace an `HttpOnly` cookie
   * of the same name — so an `HttpOnly` nonce would wedge every subsequent
   * sign-in for its whole TTL. It is not a credential: same-origin JS may read
   * it (and could mint its own anyway) and a cross-origin page never can,
   * which is exactly the property the check depends on. `/auth/sign-in` writes
   * it server-side now, but the attribute stays as it was so a page that still
   * writes one is not silently ignored.
   */
  buildStateCookie: (state: string, secure: boolean) => string;
  /** Clears the nonce — it is single-use. */
  clearStateCookie: (secure: boolean) => string;
  /** The raw session JWT, or `undefined` when absent. */
  readSession: (cookies: Record<string, string>) => string | undefined;
  /** The pending sign-in nonce, or `null` when absent or malformed. */
  readState: (cookies: Record<string, string>) => string | null;
  /** The current attempt count, coercing junk to 0. */
  readTries: (cookies: Record<string, string>) => number;
  checkState: (stored: string | null, presented: string | null) => StateCheck;
  isWellFormedState: (value: string) => boolean;
  decodeJwtExp: (jwt: string) => number | null;
  sessionMaxAge: (exp: number, nowSeconds: number) => number;
}

/** Bind the cookie layer to one app's cookie names and `Path`. */
export function createCookieKit(
  prefix: string,
  path: string = DEFAULT_BASE_PATH,
): CookieKit {
  const names = cookieNamesFor(prefix);
  return {
    names,
    parseCookies,
    buildSessionCookie: (jwt, maxAge, secure) =>
      serialize(names.session, jwt, path, maxAge, secure),
    clearSessionCookie: (secure) =>
      serialize(names.session, "", path, 0, secure),
    buildTryCookie: (count, secure) =>
      serialize(names.try, String(count), path, TRY_TTL_SECONDS, secure),
    clearTryCookie: (secure) => serialize(names.try, "", path, 0, secure),
    buildStateCookie: (state, secure) =>
      serialize(names.state, state, path, STATE_TTL_SECONDS, secure, false),
    clearStateCookie: (secure) =>
      serialize(names.state, "", path, 0, secure, false),
    readSession: (cookies) => cookies[names.session],
    readState: (cookies) => {
      const raw = cookies[names.state];
      if (raw === undefined || !isWellFormedState(raw)) return null;
      return raw;
    },
    readTries: (cookies) => {
      const raw = cookies[names.try];
      if (raw === undefined) return 0;
      const n = Number.parseInt(raw, 10);
      return Number.isFinite(n) && n > 0 ? n : 0;
    },
    checkState,
    isWellFormedState,
    decodeJwtExp,
    sessionMaxAge,
  };
}
